import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Settings, 
  Clock, 
  Play, 
  CheckCircle, 
  XCircle,
  RefreshCw,
  Edit2,
  AlertCircle,
  Calendar
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface CronJob {
  jobid: number;
  jobname: string;
  schedule: string;
  active: boolean;
  command: string;
}

interface CronJobLog {
  id: string;
  job_name: string;
  executed_at: string;
  success: boolean;
  records_affected: number;
  error_message: string | null;
  execution_time_ms: number | null;
}

// Map of cron job names to friendly names
const JOB_FRIENDLY_NAMES: Record<string, { label: string; description: string }> = {
  'process-scheduled-emails-job': {
    label: 'Processar Emails Agendados',
    description: 'Envia emails que estão na fila de agendados'
  },
  'schedule-winback-emails-job': {
    label: 'Agendar Winback',
    description: 'Verifica usuários inativos e agenda emails de winback'
  },
  'schedule-daily-digest-job': {
    label: 'Agendar Daily Digest',
    description: 'Agenda resumo diário para usuários com atividade'
  },
  'schedule-subscription-alerts-job': {
    label: 'Alertas de Assinatura',
    description: 'Alerta usuários sobre expiração de plano'
  },
  'cleanup-test-users-job': {
    label: 'Limpar Usuários de Teste',
    description: 'Remove dados de usuários test-* antigos'
  },
  'update-offline-users-job': {
    label: 'Atualizar Status Offline',
    description: 'Marca usuários inativos como offline'
  },
};

// Common cron expression presets
const CRON_PRESETS = [
  { label: 'A cada 5 minutos', value: '*/5 * * * *' },
  { label: 'A cada 15 minutos', value: '*/15 * * * *' },
  { label: 'A cada 30 minutos', value: '*/30 * * * *' },
  { label: 'A cada hora', value: '0 * * * *' },
  { label: 'Todo dia às 8h', value: '0 8 * * *' },
  { label: 'Todo dia às 18h', value: '0 18 * * *' },
  { label: 'Desabilitado (nunca)', value: '0 0 30 2 *' },
];

const EmailCronJobsPanel = () => {
  const queryClient = useQueryClient();
  const [editingJob, setEditingJob] = useState<CronJob | null>(null);
  const [newSchedule, setNewSchedule] = useState('');
  const [triggeringJob, setTriggeringJob] = useState<string | null>(null);

  // Fetch cron jobs
  const { data: cronJobs, isLoading: jobsLoading, refetch: refetchJobs } = useQuery({
    queryKey: ['admin-email-cron-jobs'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_email_cron_jobs');
      if (error) throw error;
      return (data || []) as CronJob[];
    },
  });

  // Fetch last logs for each job
  const { data: jobLogs } = useQuery({
    queryKey: ['admin-cron-job-logs-all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cron_job_logs')
        .select('*')
        .order('executed_at', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      
      // Group by job_name, keeping only the latest
      const logsMap: Record<string, CronJobLog> = {};
      for (const log of data || []) {
        const key = log.job_name;
        if (!logsMap[key]) {
          logsMap[key] = log as CronJobLog;
        }
      }
      return logsMap;
    },
    refetchInterval: 30000,
  });

  // Toggle job mutation
  const toggleJobMutation = useMutation({
    mutationFn: async ({ jobid, active }: { jobid: number; active: boolean }) => {
      const { error } = await supabase.rpc('toggle_cron_job', {
        p_jobid: jobid,
        p_active: active,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Status do cron job atualizado');
      refetchJobs();
    },
    onError: (error: any) => {
      toast.error(`Erro ao atualizar: ${error.message}`);
    },
  });

  // Update schedule mutation
  const updateScheduleMutation = useMutation({
    mutationFn: async ({ jobid, schedule }: { jobid: number; schedule: string }) => {
      const { error } = await supabase.rpc('update_cron_job_schedule', {
        p_jobid: jobid,
        p_schedule: schedule,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Schedule atualizado com sucesso');
      setEditingJob(null);
      refetchJobs();
    },
    onError: (error: any) => {
      toast.error(`Erro ao atualizar: ${error.message}`);
    },
  });

  // Trigger job manually
  const handleTriggerJob = async (jobname: string) => {
    setTriggeringJob(jobname);
    try {
      // Map job name to edge function name
      const functionName = jobname.replace('-job', '');
      const { error } = await supabase.functions.invoke(functionName);
      
      if (error) throw error;
      toast.success(`Job "${JOB_FRIENDLY_NAMES[jobname]?.label || jobname}" executado`);
      queryClient.invalidateQueries({ queryKey: ['admin-cron-job-logs-all'] });
    } catch (error: any) {
      toast.error(`Erro ao executar: ${error.message}`);
    } finally {
      setTriggeringJob(null);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Nunca';
    return new Date(dateStr).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const parseCronExpression = (cron: string): string => {
    const preset = CRON_PRESETS.find(p => p.value === cron);
    if (preset) return preset.label;
    
    const parts = cron.split(' ');
    if (parts[4] === '30' && parts[3] === '2') return 'Desabilitado';
    if (parts[0].startsWith('*/')) return `A cada ${parts[0].slice(2)} minutos`;
    if (parts[0] === '0' && parts[1] !== '*') return `Todo dia às ${parts[1]}h`;
    return cron;
  };

  const getJobLog = (jobname: string): CronJobLog | undefined => {
    // Try to find log by job name or function name
    const functionName = jobname.replace('-job', '');
    return jobLogs?.[jobname] || jobLogs?.[functionName];
  };

  const isJobDisabled = (schedule: string) => {
    return schedule === '0 0 30 2 *';
  };

  if (jobsLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-32 bg-white/10" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-white/5 backdrop-blur-sm border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Settings className="h-5 w-5 text-luxury-pinkBright" />
            Gerenciamento de Cron Jobs
          </CardTitle>
          <p className="text-white/60 text-sm">
            Visualize, ative/desative e edite o schedule dos jobs de email automáticos.
          </p>
        </CardHeader>
      </Card>

      {/* Jobs List */}
      <div className="space-y-4">
        {cronJobs?.map((job) => {
          const info = JOB_FRIENDLY_NAMES[job.jobname] || { 
            label: job.jobname, 
            description: 'Job personalizado' 
          };
          const lastLog = getJobLog(job.jobname);
          const disabled = isJobDisabled(job.schedule);
          const isTriggering = triggeringJob === job.jobname;

          return (
            <Card 
              key={job.jobid} 
              className={`bg-white/5 backdrop-blur-sm border-white/10 ${disabled ? 'opacity-60' : ''}`}
            >
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Job Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-white font-medium">{info.label}</h3>
                      <Badge 
                        className={disabled 
                          ? 'bg-gray-500/20 text-gray-400' 
                          : job.active 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-yellow-500/20 text-yellow-400'
                        }
                      >
                        {disabled ? 'Desabilitado' : job.active ? 'Ativo' : 'Pausado'}
                      </Badge>
                    </div>
                    <p className="text-white/60 text-sm mb-3">{info.description}</p>
                    
                    <div className="flex flex-wrap items-center gap-4 text-xs">
                      <div className="flex items-center gap-1 text-white/60">
                        <Calendar className="h-3 w-3" />
                        <span>{parseCronExpression(job.schedule)}</span>
                      </div>
                      <div className="flex items-center gap-1 text-white/60">
                        <Clock className="h-3 w-3" />
                        <span>Última: {formatDate(lastLog?.executed_at || null)}</span>
                      </div>
                      {lastLog && (
                        <div className="flex items-center gap-1">
                          {lastLog.success ? (
                            <CheckCircle className="h-3 w-3 text-green-400" />
                          ) : (
                            <XCircle className="h-3 w-3 text-red-400" />
                          )}
                          <span className={lastLog.success ? 'text-green-400' : 'text-red-400'}>
                            {lastLog.success 
                              ? `${lastLog.records_affected || 0} registros` 
                              : 'Falhou'
                            }
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-white/60 text-sm">Ativo</span>
                      <Switch
                        checked={job.active && !disabled}
                        onCheckedChange={(active) => 
                          toggleJobMutation.mutate({ jobid: job.jobid, active })
                        }
                        disabled={toggleJobMutation.isPending || disabled}
                      />
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingJob(job);
                        setNewSchedule(job.schedule);
                      }}
                      className="text-white/60 hover:text-white hover:bg-white/10"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTriggerJob(job.jobname)}
                      disabled={isTriggering}
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      {isTriggering ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Error message if last execution failed */}
                {lastLog && !lastLog.success && lastLog.error_message && (
                  <div className="mt-3 p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <div className="flex items-start gap-2 text-xs text-red-400">
                      <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      <span className="break-all">{lastLog.error_message}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Empty State */}
      {(!cronJobs || cronJobs.length === 0) && (
        <Card className="bg-white/5 backdrop-blur-sm border-white/10">
          <CardContent className="p-8 text-center">
            <Settings className="h-12 w-12 mx-auto mb-4 text-white/20" />
            <p className="text-white/60">Nenhum cron job de email encontrado</p>
          </CardContent>
        </Card>
      )}

      {/* Edit Schedule Dialog */}
      <Dialog open={!!editingJob} onOpenChange={() => setEditingJob(null)}>
        <DialogContent className="bg-gray-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">
              Editar Schedule: {editingJob && (JOB_FRIENDLY_NAMES[editingJob.jobname]?.label || editingJob.jobname)}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-white/60 text-sm mb-2 block">Presets</label>
              <div className="flex flex-wrap gap-2">
                {CRON_PRESETS.map((preset) => (
                  <Button
                    key={preset.value}
                    variant={newSchedule === preset.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setNewSchedule(preset.value)}
                    className={newSchedule === preset.value 
                      ? 'bg-luxury-pinkBright text-white' 
                      : 'border-white/20 text-white/80 hover:bg-white/10'
                    }
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-white/60 text-sm mb-2 block">
                Expressão Cron (avançado)
              </label>
              <Input
                value={newSchedule}
                onChange={(e) => setNewSchedule(e.target.value)}
                placeholder="*/5 * * * *"
                className="bg-white/5 border-white/10 text-white font-mono"
              />
              <p className="text-white/40 text-xs mt-1">
                Formato: minuto hora dia mês dia_semana
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setEditingJob(null)}
              className="text-white/60 hover:text-white"
            >
              Cancelar
            </Button>
            <Button
              variant="brand-gradient"
              onClick={() => editingJob && updateScheduleMutation.mutate({
                jobid: editingJob.jobid,
                schedule: newSchedule,
              })}
              disabled={updateScheduleMutation.isPending || !newSchedule}
            >
              {updateScheduleMutation.isPending ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailCronJobsPanel;
